import { useRoute } from '#imports'

import { useUserStore } from '@backmarket/nuxt-layer-oauth/useUserStore'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { insertIf } from '@backmarket/utils/collection/insertIf'

import type {
  GetReviewCollectionResponse,
  StepId,
} from '~/api/reviews/types/GetReviewCollectionResponse'

import { useReviewCollectionSource } from '../../review-collection/composables/useReviewCollectionSource'
import type { ReviewForm, ReviewFormQuestion } from '../models/review-form'

const buildNumberOfCharactersProp = ({
  values,
  questions,
}: {
  values: ReviewForm
  questions: ReviewFormQuestion[]
}): { numberOfCharacters: number } | Record<string, never> => {
  const questionsWithText = questions.filter(
    (question) => question.type === 'textarea',
  )

  if (questionsWithText.length === 0) return {}

  const { identifier } = questionsWithText[0]
  const numberOfCharacters = values[identifier]?.toString?.()?.length

  return insertIf(!!numberOfCharacters, {
    numberOfCharacters: numberOfCharacters || 0,
  })
}

export function useReviewCollectionTracking() {
  const { trackClick } = useTracking()
  const { source } = useReviewCollectionSource()

  const { params, name } = useRoute()

  const { isUserAuthenticated } = useUserStore()

  function trackRateChange({
    rate,
    step: zeroBasedStep,
    question,
  }: {
    question: string
    rate: number
    step: number
  }) {
    // 1-based index
    const step = zeroBasedStep + 1

    trackClick({
      name: question,
      zone: `reviews_page_${step}`,
      pageType: String(name),
      reviewRate: rate,
    })
  }

  function trackFilePicker({ step: zeroBasedStep }: { step: number }) {
    // 1-based index
    const step = zeroBasedStep + 1

    trackClick({
      name: 'pick_file',
      zone: `reviews_page_${step}`,
      pageType: String(name),
    })
  }

  function trackStepSubmission({
    values,
    step: zeroBasedStep,
    stepId,
    questions,
    product,
    averageRate,
    fileCount,
  }: {
    values: ReviewForm
    step: number
    stepId: StepId | null
    questions: ReviewFormQuestion[]
    product?: GetReviewCollectionResponse['product']
    averageRate: number
    fileCount: number
  }) {
    const numberOfCharacters = buildNumberOfCharactersProp({
      values,
      questions,
    })

    // 1-based index
    const step = zeroBasedStep + 1

    trackClick({
      ...insertIf(!!stepId, { name: `review_submit_${stepId}` }),
      zone: `reviews_page_${step}`,
      pageType: String(name),
      ...numberOfCharacters,
      isAuthenticated: isUserAuthenticated.value,
      productTitle: product?.title || '',
      orderlineId: params.orderlineId,
      source: source.value,
      ...insertIf(!!averageRate, { reviewRate: averageRate }),
      ...insertIf(!!fileCount, { fileCount }),
    })
  }

  function trackEditReview({ isPositive }: { isPositive: boolean }) {
    trackClick({
      name: 'edit',
      zone: 'reviews_summary',
      pageType: String(name),
      value: isPositive ? 'positive' : 'negative',
    })
  }

  function trackSkipStep(zeroBasedStep: number) {
    // 1-based index
    const step = zeroBasedStep + 1

    trackClick({
      name: 'skip',
      zone: `reviews_page_${step}`,
      pageType: String(name),
    })
  }

  function trackGoToPreviousStep(zeroBasedStep: number) {
    // 1-based index
    const step = zeroBasedStep + 1

    trackClick({
      name: 'go_back',
      zone: `reviews_page_${step}`,
      pageType: String(name),
    })
  }

  function trackGoToHome(zeroBasedStep: number) {
    // 1-based index
    const step = zeroBasedStep + 1

    trackClick({
      name: 'go_home',
      zone: `reviews_page_${step}`,
      pageType: String(name),
    })
  }

  return {
    trackEditReview,
    trackFilePicker,
    trackGoToPreviousStep,
    trackGoToHome,
    trackRateChange,
    trackSkipStep,
    trackStepSubmission,
  }
}
